import type { DatabricksRawProtocolClient } from "./raw-protocol-client.js";

const MEMORY_STORES_PATH = "/api/2.1/unity-catalog/memory-stores";

export interface DatabricksManagedMemoryOptions {
  /** Managed agent memory is a Databricks Beta and must be enabled deliberately. */
  acknowledgeBeta: true;
}

export interface DatabricksMemoryStore {
  name: string;
  catalog_name: string;
  schema_name: string;
  full_name?: string;
  description?: string;
  owner?: string;
  memory_store_id?: string;
  securable_type?: "MEMORY_STORE";
  created_at?: number;
  updated_at?: number;
  created_by?: string;
}

export interface DatabricksMemoryStorePage {
  memory_stores?: DatabricksMemoryStore[];
  next_page_token?: string;
}

export interface DatabricksMemoryEntry {
  path: string;
  scope?: string;
  contents?: string;
  description?: string;
  memory_store_name?: string;
  has_contents?: boolean;
  create_time?: string;
  update_time?: string;
}

export interface DatabricksMemoryEntryPage {
  entries?: DatabricksMemoryEntry[];
  next_page_token?: string;
}

export type DatabricksMemoryEntryEdit =
  | { replaceAll: { contents: string } }
  | { strReplace: { oldString: string; newString: string } }
  | { insert: { text: string; line?: number } };

/**
 * Explicit managed long-term memory client. Scope is required on every entry operation and is never
 * inferred from model input, keeping tenant partitioning at the trusted application boundary.
 */
export class DatabricksManagedMemoryClient {
  constructor(
    private readonly client: DatabricksRawProtocolClient,
    options: DatabricksManagedMemoryOptions,
  ) {
    if (options.acknowledgeBeta !== true) {
      throw new Error("databricks managed memory: acknowledgeBeta must be true.");
    }
  }

  createStore(input: {
    catalog: string;
    schema: string;
    name: string;
    description?: string;
  }): Promise<DatabricksMemoryStore> {
    return this.client.post<DatabricksMemoryStore>(
      MEMORY_STORES_PATH,
      {
        catalog_name: identifier(input.catalog, "catalog"),
        schema_name: identifier(input.schema, "schema"),
        name: storeId(input.name),
        ...(input.description !== undefined ? { description: input.description } : {}),
      },
      {},
      { retry: "never" },
    );
  }

  getStore(fullName: string): Promise<DatabricksMemoryStore> {
    return this.client.get<DatabricksMemoryStore>(storePath(fullName));
  }

  listStores(input: {
    catalog: string;
    schema: string;
    pageToken?: string;
    maxResults?: number;
  }): Promise<DatabricksMemoryStorePage> {
    return this.client.get<DatabricksMemoryStorePage>(MEMORY_STORES_PATH, {
      catalog_name: identifier(input.catalog, "catalog"),
      schema_name: identifier(input.schema, "schema"),
      ...(input.pageToken ? { page_token: nonEmpty(input.pageToken, "pageToken") } : {}),
      ...(input.maxResults !== undefined
        ? { max_results: boundedInteger(input.maxResults, "maxResults", 1, 1000) }
        : {}),
    });
  }

  updateStore(fullName: string, description: string): Promise<DatabricksMemoryStore> {
    return this.client.patch<DatabricksMemoryStore>(
      storePath(fullName),
      { memory_store: { description }, update_mask: "description" },
      {},
      { retry: "safe" },
    );
  }

  deleteStore(fullName: string): Promise<void> {
    return this.client.delete<void>(storePath(fullName), undefined, {}, { retry: "never" });
  }

  createEntry(
    fullName: string,
    scope: string,
    entry: { path: string; contents?: string; description?: string },
  ): Promise<DatabricksMemoryEntry> {
    return this.client.post<DatabricksMemoryEntry>(
      entriesPath(fullName),
      {
        path: memoryPath(entry.path),
        ...(entry.contents !== undefined ? { contents: entry.contents } : {}),
        ...(entry.description !== undefined ? { description: entry.description } : {}),
      },
      { scope: memoryScope(scope) },
      { retry: "never" },
    );
  }

  getEntry(fullName: string, scope: string, path: string): Promise<DatabricksMemoryEntry> {
    return this.client.get<DatabricksMemoryEntry>(`${entriesPath(fullName)}:get`, {
      scope: memoryScope(scope),
      path: memoryPath(path),
    });
  }

  listEntries(
    fullName: string,
    scope: string,
    options: { pathPrefix?: string; pageSize?: number; pageToken?: string } = {},
  ): Promise<DatabricksMemoryEntryPage> {
    return this.client.get<DatabricksMemoryEntryPage>(entriesPath(fullName), {
      scope: memoryScope(scope),
      ...(options.pathPrefix ? { path_prefix: memoryPath(options.pathPrefix) } : {}),
      ...(options.pageSize !== undefined
        ? { page_size: boundedInteger(options.pageSize, "pageSize", 1, 1000) }
        : {}),
      ...(options.pageToken ? { page_token: nonEmpty(options.pageToken, "pageToken") } : {}),
    });
  }

  updateEntry(
    fullName: string,
    scope: string,
    path: string,
    edit: DatabricksMemoryEntryEdit,
    description?: string,
  ): Promise<DatabricksMemoryEntry> {
    return this.client.patch<DatabricksMemoryEntry>(
      entriesPath(fullName),
      {
        scope: memoryScope(scope),
        path: memoryPath(path),
        ...serializeEdit(edit),
        ...(description !== undefined ? { description } : {}),
      },
      {},
      { retry: "safe" },
    );
  }

  deleteEntry(fullName: string, scope: string, path: string): Promise<void> {
    return this.client.delete<void>(
      entriesPath(fullName),
      undefined,
      { scope: memoryScope(scope), path: memoryPath(path) },
      { retry: "safe" },
    );
  }

  search(
    fullName: string,
    input: { scope: string; query: string; pathPrefix?: string; topK?: number },
  ): Promise<DatabricksMemoryEntryPage> {
    return this.client.post<DatabricksMemoryEntryPage>(
      `${entriesPath(fullName)}:search`,
      {
        scope: memoryScope(input.scope),
        query: nonEmpty(input.query, "query"),
        ...(input.pathPrefix ? { path_prefix: memoryPath(input.pathPrefix) } : {}),
        ...(input.topK !== undefined ? { top_k: boundedInteger(input.topK, "topK", 1, 50) } : {}),
      },
      {},
      { retry: "never" },
    );
  }
}

export function databricksManagedMemory(
  client: DatabricksRawProtocolClient,
  options: DatabricksManagedMemoryOptions,
): DatabricksManagedMemoryClient {
  return new DatabricksManagedMemoryClient(client, options);
}

function storePath(fullName: string): string {
  const parts = fullName.split(".");
  if (parts.length !== 3) throw new Error("memory store name must be catalog.schema.store.");
  return `${MEMORY_STORES_PATH}/${parts.map((part) => identifier(part, "memory store name")).join(".")}`;
}

function entriesPath(fullName: string): string {
  return `${storePath(fullName)}/entries`;
}

function storeId(value: string): string {
  const normalized = nonEmpty(value, "memory store name");
  if (!/^[A-Za-z0-9_-]{1,255}$/u.test(normalized)) {
    throw new Error("memory store name must contain only letters, numbers, underscore, or hyphen.");
  }
  return normalized;
}

function identifier(value: string, name: string): string {
  const normalized = nonEmpty(value, name);
  if (!/^[A-Za-z0-9_-]{1,255}$/u.test(normalized)) {
    throw new Error(`${name} must be a Databricks identifier.`);
  }
  return normalized;
}

function memoryScope(value: string): string {
  const normalized = nonEmpty(value, "scope");
  if (normalized.length > 512) throw new Error("scope must be at most 512 characters.");
  return normalized;
}

function memoryPath(value: string): string {
  const normalized = nonEmpty(value, "path");
  if (!normalized.startsWith("/memories/") || normalized.includes("..")) {
    throw new Error("memory entry path must start with /memories/ and cannot traverse parents.");
  }
  if (normalized.length > 1024)
    throw new Error("memory entry path must be at most 1024 characters.");
  return normalized;
}

function serializeEdit(edit: DatabricksMemoryEntryEdit): Record<string, unknown> {
  if ("replaceAll" in edit) return { replace_all: { contents: edit.replaceAll.contents } };
  if ("strReplace" in edit) {
    return {
      str_replace: {
        old_str: nonEmpty(edit.strReplace.oldString, "oldString"),
        new_str: edit.strReplace.newString,
      },
    };
  }
  return {
    insert: {
      insert_text: edit.insert.text,
      ...(edit.insert.line !== undefined
        ? { insert_line: boundedInteger(edit.insert.line, "line", 0, Number.MAX_SAFE_INTEGER) }
        : {}),
    },
  };
}

function nonEmpty(value: string, name: string): string {
  const normalized = value.trim();
  if (!normalized) throw new Error(`${name} cannot be empty.`);
  return normalized;
}

function boundedInteger(value: number, name: string, min: number, max: number): number {
  if (!Number.isSafeInteger(value) || value < min || value > max) {
    throw new Error(`${name} must be an integer between ${min} and ${max}.`);
  }
  return value;
}
